import { LOGIN_CARD_BODY_CLASS, LoginCard } from "./login/LoginCard";
import { useEffect, useState } from "react";

import { Button } from "./design-system/button";
import { Link2Off } from "lucide-react";
import { Loading } from "@verse_core/shared_components/base/Loading";
import { LoginCardInput } from "./login/LoginCardInput";
import { PROVIDER_PORTAL_MAIN_COLUMN_CLASS } from "./util/providerPortalMainColumn";
import { faKey } from "@fortawesome/free-solid-svg-icons";
import { rebrandEnabled } from "./util/brandingFlag";

/** Set-new-password form reached from the reset email link. */
export default function ResetPassword() {
  const rebrand = rebrandEnabled();
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessage2, setErrorMessage2] = useState("");
  const [errorMessage3, setErrorMessage3] = useState("");
  const [tokenStatus, setTokenStatus] = useState<
    "loading" | "valid" | "expired" | "invalid"
  >("loading");

  const query = new URLSearchParams(window.location.search);
  const resetPasswordToken = query.get("reset_password_token");

  useEffect(() => {
    if (!resetPasswordToken) {
      setTokenStatus("invalid");
      return;
    }
    fetch(
      `/graphql/check-reset-token?token=${encodeURIComponent(resetPasswordToken)}`,
    )
      .then((r) => r.json())
      .then((res) => {
        if (res.ok) setTokenStatus("valid");
        else if (res.expired) setTokenStatus("expired");
        else setTokenStatus("invalid");
      })
      .catch(() => setTokenStatus("invalid"));
  }, []);

  useEffect(() => {
    if (password.length > 0 && password.length < 12) {
      setErrorMessage("Password is less than 12 characters.");
    } else {
      setErrorMessage("");
    }
    if (password !== passwordConfirmation) {
      setErrorMessage2("Password entries do not match.");
    } else {
      setErrorMessage2("");
    }
  }, [password, passwordConfirmation]);

  const resetPassword = (newPassword: string, passwordConfirmation: string) => {
    if (newPassword.length >= 12 && newPassword === passwordConfirmation) {
      fetch("/graphql/reset-password", {
        method: "POST",
        body: JSON.stringify({
          reset_password_token: resetPasswordToken,
          new_password: newPassword,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((res) => {
          if (res.redirected) {
            window.location.href = res.url;
            return null;
          }
          return res.json();
        })
        .then((res) => {
          if (res && res.ok === false) {
            setErrorMessage3(res.data);
          }
        });
    }
  };

  if (tokenStatus === "loading") return <Loading fullscreen />;

  if (tokenStatus === "expired" && rebrand) {
    return (
      <LoginCard heading="This invite link has expired">
        <p className={LOGIN_CARD_BODY_CLASS}>
          Invite links are valid for 72 hours. Please request a new invite from
          your admin or clinic contact.
        </p>
      </LoginCard>
    );
  }

  if (tokenStatus === "expired") {
    return (
      <div
        className={`${PROVIDER_PORTAL_MAIN_COLUMN_CLASS} tw-mt-[90px] tw-flex tw-items-center tw-justify-center`}
      >
        <div className="tw-flex tw-w-1/4 tw-flex-col tw-items-center tw-gap-[10px] tw-rounded-[10px] tw-border tw-border-gray-100 tw-bg-white tw-p-[30px] tw-text-center tw-shadow">
          <Link2Off
            className="tw-h-[40px] tw-w-[40px] tw-text-blue-700"
            strokeWidth={1.5}
          />
          <h2 className="tw-text-[23px] tw-font-semibold tw-text-blue-950">
            This invite link has expired
          </h2>
          <p className="tw-text-[13px] tw-text-gray-500">
            Invite links are valid for 72 hours. Please request a new invite
            from your admin or clinic contact.
          </p>
        </div>
      </div>
    );
  }

  if (rebrand) {
    return (
      <LoginCard heading="Set a new password">
        <form
          className="tw-flex tw-flex-col tw-gap-[24px]"
          onSubmit={(e) => {
            e.preventDefault();
            resetPassword(password, passwordConfirmation);
          }}
        >
          <p className={LOGIN_CARD_BODY_CLASS}>
            For the security of your account, passwords must be 12 or more
            characters. Special characters are permitted but not required. We
            suggest using a unique password not used elsewhere. If you forget
            your password, you can reset your password at any time by requesting
            a password reset email.
          </p>
          <div className="tw-flex tw-flex-col tw-gap-[12px]">
            <LoginCardInput
              id="reset-password"
              icon={faKey}
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hasError={!!errorMessage}
              autoFocus
            />
            <LoginCardInput
              id="reset-password-confirmation"
              icon={faKey}
              type="password"
              placeholder="Confirm new password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              hasError={!!errorMessage2}
            />
          </div>
          {(errorMessage || errorMessage2 || errorMessage3) && (
            <div
              className="tw-font-sohne tw-text-[14px] tw-text-verse-text-critical"
              role="alert"
            >
              <p className="tw-m-0">{errorMessage}</p>
              <p className="tw-m-0">{errorMessage2}</p>
              <p className="tw-m-0">{errorMessage3}</p>
            </div>
          )}
          <Button
            type="submit"
            colorScheme="tide"
            size="lg"
            className="tw-w-full"
            disabled={password != passwordConfirmation && password.length >= 12}
          >
            Finish
          </Button>
        </form>
      </LoginCard>
    );
  }

  return (
    <div className={PROVIDER_PORTAL_MAIN_COLUMN_CLASS} id="login_row">
      <div className="tw-mx-auto tw-w-1/3 1040:tw-w-[90%]">
        <div className="panel panel-default tw-mt-[80px]">
          <div className="panel-heading">
            <h3 className="panel-title">Forgot Password?</h3>
          </div>
          <div id="existing-customer" className="panel-body">
            <div className="unobtrusive-flash-container"></div>
            <div>
              <div
                className="unobtrusive-flash-container"
                id="login-error"
              ></div>

              <p>
                For the security of your account, passwords must be 12 or more
                characters. Special characters are permitted but not required.
                We suggest using a unique password not used elsewhere. If you
                forget your password, you can reset your password at any time by
                requesting a password reset email.
              </p>

              <fieldset id="password-credentials">
                <div className="tw-mb-[15px]">
                  <input
                    className="form-control"
                    type="password"
                    placeholder="New Password"
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                  />
                </div>
                <div className="tw-mb-[15px]">
                  <input
                    className="form-control"
                    type="password"
                    placeholder="New Password Confirmation"
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    value={passwordConfirmation}
                  />
                </div>
                <div className="tw-text-red-1">
                  <p>{errorMessage}</p>
                  <p>{errorMessage2}</p>
                  <p>{errorMessage3}</p>
                </div>
                <div className="tw-mb-[15px]">
                  <button
                    className="btn btn-lg btn-success btn-block"
                    type="submit"
                    onClick={() =>
                      resetPassword(password, passwordConfirmation)
                    }
                    disabled={
                      password != passwordConfirmation && password.length >= 12
                    }
                  >
                    Finish
                  </button>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
